import { useMemo } from "react";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { BrandMediaGenerateInput, BrandMediaItem } from "@/types/brandMedia.type";
import type {
  WorkshopItemAttribution,
  WorkshopMediaLibraryLink,
} from "@/types/workshop.type";
import {
  MAX_BRAND_MEDIA_BYTES,
  brandMediaKey,
  extensionOf,
  imageMimeFor,
  readImageDimensions,
} from "../../api/useBrandMedia";
import { workshopMediaKey } from "./workshopKeys";

/**
 * An image the project owns — uploaded or generated here, or copied in from
 * the brand library. Shaped like a library item so the shared picker and cards
 * render it unchanged.
 */
export type WorkshopMediaItem = BrandMediaItem & {
  projectId: string;
  linkedAt: string;
  /** The library image this was added from (also a pre-copy library id). */
  copiedFromBrandMediaId: string | null;
  /** Non-null when the image already exists in the brand library. */
  library: WorkshopMediaLibraryLink | null;
  /** Who put the image in this project. */
  attribution?: WorkshopItemAttribution;
  /** Client-only: an optimistic row still waiting on the server. */
  pending?: boolean;
};

type UploadUrlResponse = { mediaId: string; uploadUrl: string; uploadToken: string };

/**
 * The images of ONE project — never the brand library.
 *
 * Never polled, for the same reason as the library: every row carries a
 * presigned URL a refetch would rotate.
 */
export function workshopMediaQuery(projectId: string | null) {
  return queryOptions({
    queryKey: workshopMediaKey(projectId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ media: WorkshopMediaItem[] }>(
        `/workshop/projects/${projectId}/media`,
      );
      return res.data.media;
    },
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });
}

export function useWorkshopMedia(projectId: string | null) {
  return useQuery(workshopMediaQuery(projectId));
}

function tempId(): string {
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useWorkshopMediaMutations(projectId: string | null) {
  const qc = useQueryClient();
  const key = workshopMediaKey(projectId);

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  /** Put a row back exactly once — the list may have moved on since. */
  const restore = (row: WorkshopMediaItem | null, index: number) => {
    if (!row) return;
    qc.setQueryData<WorkshopMediaItem[]>(key, (list = []) => {
      if (list.some((m) => m.id === row.id)) return list;
      const next = [...list];
      next.splice(Math.min(index, next.length), 0, row);
      return next;
    });
  };

  const dropRow = (id: string) =>
    qc.setQueryData<WorkshopMediaItem[]>(key, (list) => list?.filter((m) => m.id !== id));

  const addRow = (row: WorkshopMediaItem) =>
    qc.setQueryData<WorkshopMediaItem[]>(key, (list = []) => [row, ...list]);

  /**
   * Upload an image INTO the project. `sessionId` = the chat thread it was
   * attached in. The row shows immediately from the local file.
   */
  const upload = useMutation({
    mutationFn: async (vars: { file: File; sessionId?: string | null; previewId: string }) => {
      const { file } = vars;
      if (file.size > MAX_BRAND_MEDIA_BYTES) {
        throw new Error(
          `Image is too large (max ${Math.floor(MAX_BRAND_MEDIA_BYTES / (1024 * 1024))} MB)`,
        );
      }
      const mimetype = imageMimeFor(file);
      const dimensions = await readImageDimensions(file);
      const { data } = await axiosInstance.post<UploadUrlResponse>(
        `/workshop/projects/${projectId}/media/upload-url`,
        {
          filename: file.name,
          mimetype,
          extension: extensionOf(file.name) || "jpg",
          sessionId: vars.sessionId ?? null,
        },
      );
      // The PUT must carry exactly the Content-Type the presign was minted with.
      const putRes = await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": mimetype },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      const confirmed = await axiosInstance.post<{ media: WorkshopMediaItem }>(
        `/workshop/projects/${projectId}/media/confirm`,
        { uploadToken: data.uploadToken, ...dimensions },
      );
      return confirmed.data.media;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const localUrl = URL.createObjectURL(vars.file);
      addRow({
        id: vars.previewId,
        projectId: projectId ?? "",
        brandId: "",
        kind: "IMAGE",
        fileName: vars.file.name,
        mimeType: vars.file.type,
        sizeBytes: vars.file.size,
        width: null,
        height: null,
        isAiGenerated: false,
        prompt: null,
        model: null,
        createdByUserId: null,
        createdAt: new Date().toISOString(),
        linkedAt: new Date().toISOString(),
        url: localUrl,
        thumbUrl: null,
        copiedFromBrandMediaId: null,
        library: null,
        pending: true,
      });
      return { localUrl };
    },
    onSuccess: (media, vars) => {
      // Swap the placeholder for the real row in place, then reconcile.
      qc.setQueryData<WorkshopMediaItem[]>(key, (list = []) =>
        list.map((m) => (m.id === vars.previewId ? media : m)),
      );
    },
    onError: (error, vars) => {
      dropRow(vars.previewId);
      queue.add({
        title: `Couldn't upload ${vars.file.name}`,
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: (_data, _error, _vars, context) => {
      if (context?.localUrl) URL.revokeObjectURL(context.localUrl);
      void invalidate();
    },
  });

  /** Generate INTO the project — the picker holds its own loading state meanwhile. */
  const generate = useMutation({
    mutationFn: async (input: BrandMediaGenerateInput) => {
      const { data } = await axiosInstance.post<{ media: WorkshopMediaItem }>(
        `/workshop/projects/${projectId}/media/generate`,
        input,
      );
      return data.media;
    },
    onSuccess: (media) => {
      qc.setQueryData<WorkshopMediaItem[]>(key, (list = []) =>
        list.some((m) => m.id === media.id) ? list : [media, ...list],
      );
      void invalidate();
    },
  });

  /** Copy a brand-library image into the project. Refused if it's already here. */
  const addFromLibrary = useMutation({
    mutationFn: async (vars: { item: BrandMediaItem; previewId: string }) => {
      const { data } = await axiosInstance.post<{ media: WorkshopMediaItem }>(
        `/workshop/projects/${projectId}/media`,
        { mediaId: vars.item.id },
      );
      return data.media;
    },
    onMutate: async ({ item, previewId }) => {
      await qc.cancelQueries({ queryKey: key });
      addRow({
        ...item,
        id: previewId,
        projectId: projectId ?? "",
        linkedAt: new Date().toISOString(),
        copiedFromBrandMediaId: item.id,
        library: null,
        pending: true,
      });
    },
    onSuccess: (media, vars) => {
      qc.setQueryData<WorkshopMediaItem[]>(key, (list = []) =>
        list.map((m) => (m.id === vars.previewId ? media : m)),
      );
    },
    onError: (error, vars) => {
      dropRow(vars.previewId);
      queue.add({
        title: "Couldn't add the image",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: () => void invalidate(),
  });

  /** Removes the image from THIS PROJECT (its row). A library copy is untouched. */
  const remove = useMutation({
    mutationKey: ["workshopMediaRemove", projectId],
    mutationFn: async (mediaId: string) => {
      await axiosInstance.delete(`/workshop/projects/${projectId}/media/${mediaId}`);
    },
    onMutate: async (mediaId) => {
      await qc.cancelQueries({ queryKey: key });
      const list = qc.getQueryData<WorkshopMediaItem[]>(key) ?? [];
      const index = list.findIndex((m) => m.id === mediaId);
      dropRow(mediaId);
      return { row: index >= 0 ? list[index] : null, index };
    },
    onError: (error, _mediaId, context) => {
      restore(context?.row ?? null, context?.index ?? 0);
      queue.add({
        title: "Couldn't remove the image",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: () => {
      if (qc.isMutating({ mutationKey: ["workshopMediaRemove", projectId] }) > 1) return;
      void invalidate();
    },
  });

  /**
   * "Copy to brand library". The caller checks `item.library` first and tells
   * the user instead of calling; a 409 here means someone else just copied it.
   */
  const copyToLibrary = useMutation({
    mutationFn: async (vars: { item: WorkshopMediaItem; by: WorkshopMediaLibraryLink["by"] }) => {
      const { data } = await axiosInstance.post<{
        libraryMedia: BrandMediaItem;
        media: WorkshopMediaItem | null;
      }>(`/workshop/projects/${projectId}/media/${vars.item.id}/copy-to-library`);
      return data;
    },
    onMutate: async ({ item, by }) => {
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<WorkshopMediaItem[]>(key, (list) =>
        list?.map((m) =>
          m.id === item.id
            ? { ...m, library: { mediaId: "", relation: "copied_to", by } }
            : m,
        ),
      );
      return { previous: item.library };
    },
    onSuccess: (data, { item }) => {
      if (data.media) {
        qc.setQueryData<WorkshopMediaItem[]>(key, (list) =>
          list?.map((m) => (m.id === item.id ? data.media! : m)),
        );
      }
      void qc.invalidateQueries({ queryKey: brandMediaKey(data.libraryMedia.brandId) });
    },
    onError: (error, { item }, context) => {
      qc.setQueryData<WorkshopMediaItem[]>(key, (list) =>
        list?.map((m) => (m.id === item.id ? { ...m, library: context?.previous ?? null } : m)),
      );
      queue.add({
        title: "Couldn't copy to the brand library",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: () => void invalidate(),
  });

  return { upload, generate, addFromLibrary, remove, copyToLibrary };
}

/**
 * Adapter for the shared media picker: its Upload and Generate tabs create the
 * image in THIS project instead of the brand library.
 */
export function useWorkshopMediaCreateTarget(projectId: string | null) {
  const { upload, generate } = useWorkshopMediaMutations(projectId);
  return useMemo(
    () => ({
      upload: {
        mutateAsync: (file: File) => upload.mutateAsync({ file, previewId: tempId() }),
        isPending: upload.isPending,
      },
      generate: {
        mutateAsync: (input: BrandMediaGenerateInput) => generate.mutateAsync(input),
        isPending: generate.isPending,
      },
      uploadDescription: "Upload an image into this project. It stays out of the brand library.",
      generateDescription: "Generated pictures are saved to this project, not the brand library.",
    }),
    [upload, generate],
  );
}

export { tempId as newMediaPreviewId };
